import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  Heart,
  MessageCircle,
  Share2,
  Send,
  TrendingUp,
  Users,
  UserPlus,
  UserCheck,
  Image as ImageIcon,
  FileText,
  Bookmark,
  MoreHorizontal,
  Bell,
  Globe,
  Loader2,
  Check,
  X,
  Clock,
  Paperclip,
  Trash2,
  Hash,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import { connectionService } from "../services/connectionService";
import { socialService } from "../services/socialService";
import cloudService from "../services/cloudService";

function PostMedia({ mediaUrl, mediaType }: { mediaUrl?: string; mediaType?: string }) {
  const [presignedUrl, setPresignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!mediaUrl) { setLoading(false); return; }
    let mounted = true;
    cloudService.getDownloadPresignedUrl(mediaUrl)
      .then((res: any) => { if (mounted) setPresignedUrl(res.presignedUrl || res.url || res); })
      .catch(() => { if (mounted) setPresignedUrl(null); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [mediaUrl]);

  if (!mediaUrl || loading) return null;

  if (mediaType === "IMAGE") {
    return (
      <div className="rounded-md overflow-hidden bg-muted">
        {presignedUrl ? (
          <img src={presignedUrl} alt="Post media" className="w-full max-h-96 object-contain" />
        ) : (
          <div className="p-4 text-center text-sm text-muted-foreground">Image unavailable</div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 p-4 bg-muted rounded-md">
      <FileText className="h-5 w-5 text-primary flex-shrink-0" />
      <span className="text-sm flex-1 truncate">{mediaUrl.split('/').pop()}</span>
      <Button size="sm" variant="outline" onClick={() => {
        if (presignedUrl) window.open(presignedUrl, '_blank');
        else cloudService.downloadFile(mediaUrl);
      }}>
        Download
      </Button>
    </div>
  );
}

const PREDEFINED_TOPICS = [
  { name: "ESGInvesting", label: "ESG Investing", category: "Investment" },
  { name: "MarketOutlook", label: "Market Outlook", category: "Analysis" },
  { name: "TaxPlanning", label: "Tax Planning", category: "Planning" },
  { name: "RetirementPlanning", label: "Retirement Planning", category: "Planning" },
  { name: "WealthManagement", label: "Wealth Management", category: "Advisory" },
  { name: "RiskManagement", label: "Risk Management", category: "Advisory" },
  { name: "QuantAnalysis", label: "Quantitative Analysis", category: "Analysis" },
  { name: "FinTech", label: "FinTech", category: "Technology" },
  { name: "RealEstate", label: "Real Estate", category: "Investment" },
  { name: "Cryptocurrency", label: "Cryptocurrency", category: "Investment" },
];

export function SocialNetworking() {
  const userId = localStorage.getItem("userId");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);
  const [feedError, setFeedError] = useState<string | null>(null);
  const [posts, setPosts] = useState<any[]>([]);
  const [trendingPosts, setTrendingPosts] = useState<any[]>([]);
  const [trendingLoading, setTrendingLoading] = useState(false);
  const [bookmarkedPosts, setBookmarkedPosts] = useState<any[]>([]);
  const [bookmarkedLoading, setBookmarkedLoading] = useState(false);
  const [trendingTopics, setTrendingTopics] = useState<any[]>([]);
  const [trendingTopicsLoading, setTrendingTopicsLoading] = useState(false);
  const [connections, setConnections] = useState<any[]>([]);
  const [pendingRequests, setPendingRequests] = useState<any[]>([]);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [discoverUsers, setDiscoverUsers] = useState<any[]>([]);
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const [discoverPage, setDiscoverPage] = useState(0);
  const [discoverByRole, setDiscoverByRole] = useState<any>(null);
  const [discoverByRoleLoading, setDiscoverByRoleLoading] = useState(false);
  const [sentRequests, setSentRequests] = useState<any[]>([]);
  const [newPostContent, setNewPostContent] = useState("");
  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(new Set());
  const [submittingPost, setSubmittingPost] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [feedTab, setFeedTab] = useState("all");
  const [commentInputs, setCommentInputs] = useState<Record<string, string>>({});
  const [postComments, setPostComments] = useState<Record<string, any[]>>({});
  const [showComments, setShowComments] = useState<Record<string, boolean>>({});
  const [commentsLoading, setCommentsLoading] = useState<Record<string, boolean>>({});
  const [sharingPost, setSharingPost] = useState<string | null>(null);
  const [shareComment, setShareComment] = useState("");
  const [messagingConnection, setMessagingConnection] = useState<any>(null);
  const [messageText, setMessageText] = useState("");
  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [feedRes, connRes, pendingRes, suggestRes, trendingRes, sentRes, discoverRes] = await Promise.allSettled([
        socialService.getFeed({ page: 0, size: 20 }),
        userId ? connectionService.getConnections(userId, {}) : Promise.resolve(null),
        userId ? connectionService.getPendingRequests(userId) : Promise.resolve([]),
        userId ? connectionService.getConnectionSuggestions({ limit: 10 }) : Promise.resolve([]),
        userId ? socialService.getTrendingTopics() : Promise.resolve([]),
        userId ? connectionService.getSentRequests(userId) : Promise.resolve([]),
        userId ? connectionService.discoverUsersByRole(userId) : Promise.resolve(null),
      ]);

      if (feedRes.status === "fulfilled" && feedRes.value) {
        const feedData = feedRes.value.posts || feedRes.value.content || [];
        setPosts(feedData);
        setFeedError(null);
      } else if (feedRes.status === "rejected") {
        console.error("Feed API error:", feedRes.reason);
        setFeedError(feedRes.reason?.message || "Failed to load feed");
      }
      if (connRes.status === "fulfilled" && connRes.value) {
        const list = connRes.value.connections || connRes.value.content || [];
        setConnections(list.filter((c: any) => c.status === "ACTIVE" || c.status === "active"));
      }
      if (pendingRes.status === "fulfilled" && pendingRes.value) {
        setPendingRequests(Array.isArray(pendingRes.value) ? pendingRes.value : []);
      }
      if (suggestRes.status === "fulfilled" && suggestRes.value) {
        setSuggestions(Array.isArray(suggestRes.value) ? suggestRes.value : suggestRes.value.content || []);
      }
      if (trendingRes.status === "fulfilled" && trendingRes.value) {
        setTrendingTopics(Array.isArray(trendingRes.value) ? trendingRes.value : []);
      }
      if (sentRes.status === "fulfilled" && sentRes.value) {
        setSentRequests(Array.isArray(sentRes.value) ? sentRes.value : []);
      }
      if (discoverRes.status === "fulfilled" && discoverRes.value) {
        setDiscoverByRole(discoverRes.value);
      }
    } catch (e) {
      console.error("Failed to load network data", e);
    } finally {
      setLoading(false);
    }
  };

  const loadSidebarData = async () => {
    if (!userId) return;
    try {
      const [connRes, pendingRes, sentRes, discoverRes] = await Promise.allSettled([
        connectionService.getConnections(userId, {}),
        connectionService.getPendingRequests(userId),
        connectionService.getSentRequests(userId),
        connectionService.discoverUsersByRole(userId),
      ]);
      if (connRes.status === "fulfilled" && connRes.value) {
        const list = connRes.value.connections || connRes.value.content || [];
        setConnections(list.filter((c: any) => c.status === "ACTIVE" || c.status === "active"));
      }
      if (pendingRes.status === "fulfilled" && pendingRes.value) {
        setPendingRequests(Array.isArray(pendingRes.value) ? pendingRes.value : []);
      }
      if (sentRes.status === "fulfilled" && sentRes.value) {
        setSentRequests(Array.isArray(sentRes.value) ? sentRes.value : []);
      }
      if (discoverRes.status === "fulfilled" && discoverRes.value) {
        setDiscoverByRole(discoverRes.value);
      }
    } catch (e) {
      console.error("Failed to load sidebar data", e);
    }
  };

  const loadDiscover = async (page: number) => {
    if (!userId) return;
    setDiscoverLoading(true);
    try {
      const users = await connectionService.discoverUsers(userId, { page, size: 20 });
      const list = Array.isArray(users) ? users : (users.content || []);
      if (page === 0) {
        setDiscoverUsers(list);
      } else {
        setDiscoverUsers(prev => [...prev, ...list]);
      }
      setDiscoverPage(page);
    } catch (e) {
      console.error("Failed to load discover users", e);
    } finally {
      setDiscoverLoading(false);
    }
  };

  const loadTrending = async () => {
    setTrendingLoading(true);
    try {
      const posts = await socialService.getTrendingPosts({ hours: 24, limit: 20 });
      setTrendingPosts(Array.isArray(posts) ? posts : []);
    } catch (e) {
      console.error("Failed to load trending posts", e);
      setTrendingPosts([]);
    } finally {
      setTrendingLoading(false);
    }
  };

  const loadBookmarkedPosts = async () => {
    setBookmarkedLoading(true);
    try {
      const posts = await socialService.getBookmarkedPosts({ page: 0, size: 20 });
      setBookmarkedPosts(Array.isArray(posts) ? posts : []);
    } catch (e) {
      console.error("Failed to load bookmarked posts", e);
      setBookmarkedPosts([]);
    } finally {
      setBookmarkedLoading(false);
    }
  };

  const toggleTopic = (topic: { name: string; label: string }) => {
    const tag = `#${topic.name}`;
    const isSelected = selectedTopics.has(topic.name);
    setSelectedTopics(prev => {
      const next = new Set(prev);
      if (isSelected) next.delete(topic.name);
      else next.add(topic.name);
      return next;
    });
    setNewPostContent(prev => {
      if (isSelected) {
        const regex = new RegExp(`\\s*${tag}`, 'g');
        return prev.replace(regex, '').trim();
      }
      return `${prev} ${tag}`.trim();
    });
  };

  const handleLikePost = async (postId: string, hasLiked: boolean) => {
    try {
      if (hasLiked) await socialService.unlikePost(postId);
      else await socialService.likePost(postId);
      const toggle = (p: any) => {
        if ((p.postId || p.id) === postId) {
          return { ...p, likedByUser: !hasLiked, likesCount: hasLiked ? Math.max(0, (p.likesCount || 0) - 1) : (p.likesCount || 0) + 1 };
        }
        return p;
      };
      setPosts(prev => prev.map(toggle));
      setTrendingPosts(prev => prev.map(toggle));
      setBookmarkedPosts(prev => prev.map(toggle));
    } catch (e) {
      toast.error("Failed to update like");
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setAttachedFile(file);
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim() && !attachedFile) return;
    setSubmittingPost(true);
    try {
      let mediaUrl = null;
      let mediaType = null;

      if (attachedFile) {
        setUploadingFile(true);
        const { key } = await cloudService.uploadFile(attachedFile, "post-media");
        mediaUrl = key;
        mediaType = attachedFile.type.startsWith("image/") ? "IMAGE" : "DOCUMENT";
        setUploadingFile(false);
      }

      const created = await socialService.createPost({
        content: newPostContent,
        type: mediaType || "TEXT",
        visibility: "PUBLIC",
        mediaUrl,
      });
      toast.success("Post created");
      setNewPostContent("");
      setAttachedFile(null);
      setSelectedTopics(new Set());
      setPosts([created, ...posts]);
    } catch (e) {
      toast.error("Failed to create post");
    } finally {
      setSubmittingPost(false);
    }
  };

  const handleSendRequest = async (recipientId: string) => {
    try {
      const res = await connectionService.sendConnectionRequest({ recipientId, requesterId: userId });
      toast.success("Connection request sent");
      const connId = res?.connectionId || res?.id;
      setDiscoverUsers(prev => prev.filter((u: any) => (u.userId || u.id) !== recipientId));
      setDiscoverByRole((prev: any) => {
        if (!prev) return prev;
        const remove = (users: any[]) => users.filter((u: any) => (u.userId || u.id) !== recipientId);
        return { investors: remove(prev.investors || []), advisors: remove(prev.advisors || []), analysts: remove(prev.analysts || []) };
      });
      let foundUser: any = null;
      if (discoverByRole) {
        for (const key of ['investors', 'advisors', 'analysts']) {
          const u = (discoverByRole[key] || []).find((x: any) => (x.userId || x.id) === recipientId);
          if (u) { foundUser = u; break; }
        }
      }
      if (foundUser) {
        setSentRequests(prev => [...prev, { connectionId: connId, userId: recipientId, fullName: foundUser.fullName, role: foundUser.role, company: foundUser.company }]);
      } else {
        loadSidebarData();
      }
    } catch (e: any) {
      toast.error(e.message || "Failed to send request");
    }
  };

  const handleAcceptRequest = async (connectionId: string) => {
    try {
      await connectionService.acceptConnectionRequest(userId!, connectionId);
      toast.success("Connection accepted");
      setPendingRequests(prev => prev.filter((r: any) => (r.connectionId || r.id) !== connectionId));
      setSentRequests(prev => prev.filter((r: any) => (r.connectionId || r.id) !== connectionId));
      loadSidebarData();
    } catch (e) {
      toast.error("Failed to accept request");
    }
  };

  const handleRejectRequest = async (connectionId: string) => {
    try {
      await connectionService.rejectConnectionRequest(userId!, connectionId);
      toast.success("Request rejected");
      setPendingRequests(prev => prev.filter((r: any) => (r.connectionId || r.id) !== connectionId));
      loadSidebarData();
    } catch (e) {
      toast.error("Failed to reject request");
    }
  };

  const handleCancelRequest = async (connectionId: string) => {
    try {
      await connectionService.cancelConnection(userId!, connectionId);
      toast.success("Request cancelled");
      setSentRequests(prev => prev.filter((r: any) => (r.connectionId || r.id) !== connectionId));
    } catch (e) {
      toast.error("Failed to cancel request");
    }
  };

  const handleSendMessage = async () => {
    if (!messagingConnection || !messageText.trim()) return;
    try {
      const connId = messagingConnection.connectionId || messagingConnection.id;
      await connectionService.sendMessageToConnection(userId!, connId, messageText.trim());
      toast.success("Message sent");
      setMessagingConnection(null);
      setMessageText("");
    } catch (e: any) {
      toast.error(e.message || "Failed to send message");
    }
  };

  const handleRemoveConnection = async (connectionId: string) => {
    try {
      await connectionService.removeConnection(userId!, connectionId);
      toast.success("Connection removed");
      loadSidebarData();
    } catch (e) {
      toast.error("Failed to remove connection");
    }
  };

  const loadComments = async (postId: string) => {
    setCommentsLoading(prev => ({ ...prev, [postId]: true }));
    try {
      const res = await socialService.getComments(postId, { page: 0, size: 50 });
      setPostComments(prev => ({ ...prev, [postId]: Array.isArray(res) ? res : [] }));
    } catch { setPostComments(prev => ({ ...prev, [postId]: [] })); }
    finally { setCommentsLoading(prev => ({ ...prev, [postId]: false })); }
  };

  const handleToggleComments = async (postId: string) => {
    if (showComments[postId]) {
      setShowComments(prev => ({ ...prev, [postId]: false }));
      return;
    }
    setShowComments(prev => ({ ...prev, [postId]: true }));
    if (!postComments[postId]) loadComments(postId);
  };

  const handleAddComment = async (postId: string) => {
    const content = commentInputs[postId];
    if (!content?.trim()) return;
    try {
      await socialService.createComment(postId, { content });
      setCommentInputs(prev => ({ ...prev, [postId]: "" }));
      loadComments(postId);
      setPosts(posts.map(p => {
        if ((p.postId || p.id) === postId) {
          return { ...p, commentsCount: (p.commentsCount || 0) + 1 };
        }
        return p;
      }));
    } catch (e) { toast.error("Failed to add comment"); }
  };

  const handleSharePost = async (postId: string) => {
    if (!shareComment && !confirm("Share this post?")) return;
    try {
      await socialService.sharePost(postId, { comment: shareComment || "", visibility: "PUBLIC" });
      toast.success("Post shared");
      setSharingPost(null);
      setShareComment("");
    } catch (e) { toast.error("Failed to share post"); }
  };

  const handleBookmarkPost = async (postId: string, isBookmarked: boolean) => {
    try {
      if (isBookmarked) {
        await socialService.unbookmarkPost(postId);
      } else {
        await socialService.bookmarkPost(postId);
      }
      const toggle = (p: any) => (p.postId || p.id) === postId ? { ...p, bookmarkedByUser: !isBookmarked } : p;
      setPosts(prev => prev.map(toggle));
      setTrendingPosts(prev => prev.map(toggle));
      setBookmarkedPosts(prev => prev.map(toggle));
    } catch (e) {
      toast.error("Failed to update bookmark");
    }
  };

  const getInitials = (name: string) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getRoleBadge = (role: string) => {
    const r = (role || "").toLowerCase();
    if (r === "investor") return "bg-blue-100 text-blue-700";
    if (r === "analyst" || r === "financial_analyst") return "bg-purple-100 text-purple-700";
    if (r === "advisor" || r === "financial_advisor") return "bg-green-100 text-green-700";
    return "";
  };

  const getPostId = (p: any) => p.postId || p.id;

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const renderPostCard = (post: any) => {
    const pid = getPostId(post);
    const authorName = post.authorName || post.fullName || post.author || "Unknown";
    return (
      <Card key={pid}>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <Avatar><AvatarFallback>{getInitials(authorName)}</AvatarFallback></Avatar>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{authorName}</span>
                  <Badge variant="secondary" className={getRoleBadge(post.authorRole || post.role)}>
                    {(post.authorRole || post.role || "Professional").replace(/_/g, " ")}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {post.createdAt ? new Date(post.createdAt).toLocaleDateString() : post.timestamp || ""}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <PostMedia mediaUrl={post.mediaUrl} mediaType={post.mediaType} />
          <p className="text-sm whitespace-pre-line">{post.content}</p>
          {(post.hashtags || post.tags)?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {(Array.isArray(post.hashtags) ? post.hashtags : post.hashtags?.split(',') || post.tags || []).map((tag: string) => (
                <Badge key={tag} variant="outline" className="text-xs">#{tag.trim()}</Badge>
              ))}
            </div>
          )}
          <Separator />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" className={post.likedByUser ? "text-red-500" : ""}
                onClick={() => handleLikePost(pid, post.likedByUser)}>
                <Heart className={`h-4 w-4 mr-1 ${post.likedByUser ? "fill-current" : ""}`} />
                {post.likesCount || post.likes || 0}
              </Button>
              <Button variant="ghost" size="sm"
                onClick={() => handleToggleComments(pid)}>
                <MessageCircle className="h-4 w-4 mr-1" />
                {post.commentsCount || post.comments || 0}
              </Button>
              <Button variant="ghost" size="sm"
                onClick={() => setSharingPost(pid)}>
                <Share2 className="h-4 w-4 mr-1" />
                {post.sharesCount || post.shares || 0}
              </Button>
            </div>
            <Button variant="ghost" size="sm" className={post.bookmarkedByUser ? "text-blue-500" : ""}
              onClick={() => handleBookmarkPost(pid, post.bookmarkedByUser)}>
              <Bookmark className={`h-4 w-4 ${post.bookmarkedByUser ? "fill-current" : ""}`} />
            </Button>
          </div>
          {showComments[pid] && (
            <div className="space-y-3 pt-2">
              <Separator />
              {commentsLoading[pid] ? (
                <div className="flex justify-center py-2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {(postComments[pid] || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-2">No comments yet</p>
                  ) : postComments[pid].map((c: any) => (
                    <div key={c.commentId || c.id} className="flex gap-2 text-sm">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-[10px]">{getInitials(c.authorName)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <span className="font-medium text-xs">{c.authorName || "User"}</span>
                        <p className="text-xs text-muted-foreground">{c.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  placeholder="Write a comment..."
                  value={commentInputs[pid] || ""}
                  onChange={(e) => setCommentInputs(prev => ({ ...prev, [pid]: e.target.value }))}
                  onKeyDown={(e: React.KeyboardEvent) => e.key === "Enter" && handleAddComment(pid)}
                  className="h-8 text-sm"
                />
                <Button size="sm" className="h-8" onClick={() => handleAddComment(pid)}>
                  <Send className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Financial Professional Network</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Connect, share insights, and collaborate with investors, advisors, and analysts
              </p>
            </div>

          </div>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Feed */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <Avatar><AvatarFallback>{getInitials(localStorage.getItem('fullName') || '')}</AvatarFallback></Avatar>
                <div className="flex-1 space-y-3">
                  <Textarea
                    placeholder="Share your insights, ask questions, or start a discussion..."
                    value={newPostContent}
                    onChange={(e) => setNewPostContent(e.target.value)}
                    className="min-h-[100px]"
                  />
                  {attachedFile && (
                    <div className="flex items-center gap-2 p-2 bg-muted rounded-md">
                      <Paperclip className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm flex-1 truncate">{attachedFile.name}</span>
                      <Button variant="ghost" size="sm" onClick={() => setAttachedFile(null)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Add topics:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {PREDEFINED_TOPICS.map((t) => {
                        const active = selectedTopics.has(t.name);
                        return (
                          <button
                            key={t.name}
                            type="button"
                            onClick={() => toggleTopic(t)}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                              active
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-background text-muted-foreground border-border hover:border-primary hover:text-primary"
                            }`}
                          >
                            {active && <Check className="h-3 w-3" />}
                            {t.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept="image/*"
                        onChange={handleFileSelect}
                      />
                      <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingFile}>
                        {uploadingFile ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ImageIcon className="h-4 w-4 mr-2" />}
                        Image
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = '.pdf,.doc,.docx,.xls,.xlsx,.txt';
                        input.onchange = (e: any) => {
                          const file = e.target.files?.[0];
                          if (file) setAttachedFile(file);
                        };
                        input.click();
                      }} disabled={uploadingFile}>
                        <FileText className="h-4 w-4 mr-2" />
                        Document
                      </Button>
                    </div>
                    <Button onClick={handleCreatePost} disabled={submittingPost || (!newPostContent.trim() && !attachedFile)}>
                      {submittingPost ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                      Post
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Tabs value={feedTab} onValueChange={(value) => { setFeedTab(value); if (value === 'trending') loadTrending(); if (value === 'bookmarked') loadBookmarkedPosts(); }} className="space-y-4">
            <TabsList>
              <TabsTrigger value="all">All Posts</TabsTrigger>
              <TabsTrigger value="trending">Trending</TabsTrigger>
              <TabsTrigger value="bookmarked">Bookmarks</TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="space-y-4">
              {feedError ? (
                <Card><CardContent className="pt-6 text-center py-12">
                  <p className="text-red-500 font-medium mb-2">Failed to load feed</p>
                  <p className="text-sm text-muted-foreground">{feedError}</p>
                  <Button variant="outline" size="sm" className="mt-4" onClick={loadAll}>Retry</Button>
                </CardContent></Card>
              ) : posts.length === 0 ? (
                <Card><CardContent className="pt-6 text-center text-muted-foreground py-12">
                  <Globe className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No posts yet. Be the first to share!</p>
                </CardContent></Card>
              ) : posts.map(renderPostCard)}
            </TabsContent>

            <TabsContent value="trending" className="space-y-4">
              {trendingLoading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
              ) : trendingPosts.length === 0 ? (
                <Card><CardContent className="pt-6 text-center text-muted-foreground py-12">
                  <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No trending posts right now</p>
                </CardContent></Card>
              ) : trendingPosts.map(renderPostCard)}
            </TabsContent>

            <TabsContent value="bookmarked" className="space-y-4">
              {bookmarkedLoading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
              ) : bookmarkedPosts.length === 0 ? (
                <Card><CardContent className="pt-6 text-center text-muted-foreground py-12">
                  <Bookmark className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No bookmarked posts yet</p>
                </CardContent></Card>
              ) : bookmarkedPosts.map(renderPostCard)}
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Discover Professionals by Role */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4" />
                Discover Professionals
              </CardTitle>
            </CardHeader>
            <CardContent>
              {discoverByRoleLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : !discoverByRole ? (
                <p className="text-sm text-muted-foreground text-center py-4">No users to discover</p>
              ) : (
                <ScrollArea className="h-[450px] pr-4">
                  {['investors', 'advisors', 'analysts'].map((group) => {
                    const users = Array.isArray(discoverByRole[group]) ? discoverByRole[group] : [];
                    const label = group === 'investors' ? 'Investors' : group === 'advisors' ? 'Advisors' : 'Analysts';
                    return (
                      <div key={group} className="mb-4 last:mb-0">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                          {label}
                        </p>
                        {users.length === 0 ? (
                          <p className="text-xs text-muted-foreground pl-1 mb-2">None found</p>
                        ) : users.map((u: any) => {
                          const uid = u.userId || u.id;
                          return (
                            <div key={uid} className="flex items-center gap-2 pb-2 mb-2 border-b last:border-0 last:mb-0">
                              <Avatar className="h-8 w-8 flex-shrink-0">
                                <AvatarFallback className="text-xs">{getInitials(u.fullName)}</AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{u.fullName}</p>
                                <p className="text-xs text-muted-foreground truncate">{u.company || ""}</p>
                              </div>
                              <Button size="sm" variant="outline" className="h-7 text-xs flex-shrink-0"
                                onClick={() => handleSendRequest(uid)}>
                                <UserPlus className="h-3 w-3 mr-1" /> Connect
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          {/* Your Network */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Your Network</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px] pr-4">
                {/* Active Connections */}
                {connections.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                      <UserCheck className="h-3 w-3" /> Active ({connections.length})
                    </p>
                    {connections.map((conn: any) => {
                      const connId = conn.connectionId || conn.id;
                      const isRequester = conn.requesterId === userId;
                      const otherName = isRequester ? conn.recipientFullName : (conn.requesterFullName || conn.fullName || conn.name);
                      const otherRole = isRequester ? conn.recipientRole : conn.requesterRole;
                      const isMessaging = messagingConnection?.connectionId === connId || messagingConnection?.id === connId;
                      return (
                        <div key={connId}>
                          <div className="flex items-center gap-2 pb-2 mb-2 border-b last:border-0">
                            <Avatar className="h-8 w-8 flex-shrink-0">
                              <AvatarFallback className="text-xs">{getInitials(otherName)}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{otherName}</p>
                              <Badge variant="outline" className={`text-[10px] ${getRoleBadge(otherRole)}`}>
                                {(otherRole || "Professional").replace(/_/g, " ")}
                              </Badge>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                                onClick={() => { setMessagingConnection(isMessaging ? null : conn); setMessageText(""); }}>
                                <Mail className={`h-3.5 w-3.5 ${isMessaging ? 'text-primary' : ''}`} />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                onClick={() => handleRemoveConnection(connId)}>
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                          {isMessaging && (
                            <div className="pl-10 pb-3 space-y-2">
                              <Textarea
                                placeholder="Type your message... (max 500 chars)"
                                value={messageText}
                                onChange={(e) => setMessageText(e.target.value.slice(0, 500))}
                                className="min-h-[60px] text-sm"
                              />
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] text-muted-foreground">{messageText.length}/500</span>
                                <div className="flex gap-2">
                                  <Button size="sm" variant="ghost" className="h-7 text-xs"
                                    onClick={() => { setMessagingConnection(null); setMessageText(""); }}>Cancel</Button>
                                  <Button size="sm" className="h-7 text-xs" onClick={handleSendMessage}
                                    disabled={!messageText.trim()}>Send</Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Pending Received */}
                {pendingRequests.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                      <Bell className="h-3 w-3 text-amber-500" /> Pending Received ({pendingRequests.length})
                    </p>
                    {pendingRequests.map((req: any) => (
                      <div key={req.connectionId || req.id} className="flex items-center gap-2 pb-2 mb-2 border-b last:border-0">
                        <Avatar className="h-8 w-8 flex-shrink-0">
                          <AvatarFallback className="text-xs">{getInitials(req.fullName || req.requesterFullName)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{req.fullName || req.requesterFullName || "Unknown"}</p>
                          <p className="text-xs text-muted-foreground truncate capitalize">{req.role?.replace(/_/g, " ") || ""}</p>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button size="sm" className="h-7 text-xs" onClick={() => handleAcceptRequest(req.connectionId || req.id)}>
                            <Check className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleRejectRequest(req.connectionId || req.id)}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pending Sent */}
                {sentRequests.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                      <Clock className="h-3 w-3 text-blue-500" /> Pending Sent ({sentRequests.length})
                    </p>
                    {sentRequests.map((req: any) => (
                      <div key={req.connectionId || req.id} className="flex items-center gap-2 pb-2 mb-2 border-b last:border-0">
                        <Avatar className="h-8 w-8 flex-shrink-0">
                          <AvatarFallback className="text-xs">{getInitials(req.fullName || req.requesterFullName || req.recipientFullName)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{req.fullName || req.recipientFullName || "Unknown"}</p>
                          <p className="text-xs text-muted-foreground truncate capitalize">{req.role?.replace(/_/g, " ") || ""}</p>
                        </div>
                        <Button size="sm" variant="outline" className="h-7 text-xs flex-shrink-0"
                          onClick={() => handleCancelRequest(req.connectionId || req.id)}>
                          Withdraw
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {connections.length === 0 && pendingRequests.length === 0 && sentRequests.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">No connections yet</p>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Trending Topics */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Hash className="h-4 w-4" />
                Trending Topics
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trendingTopicsLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : trendingTopics.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No trending topics yet</p>
              ) : (
                <div className="space-y-3">
                  {trendingTopics.map((t: any) => (
                    <div key={t.topic} className="p-3 border rounded-lg hover:bg-muted cursor-pointer transition-colors">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{t.topic}</span>
                        <TrendingUp className="h-4 w-4 text-green-500" />
                      </div>
                      <p className="text-xs text-muted-foreground">{t.postCount} posts</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Share Dialog */}
      <Dialog open={!!sharingPost} onOpenChange={() => { setSharingPost(null); setShareComment(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Post</DialogTitle>
            <DialogDescription>Add a comment to your share (optional)</DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="What do you think about this post?"
            value={shareComment}
            onChange={(e) => setShareComment(e.target.value)}
            className="min-h-[80px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSharingPost(null); setShareComment(""); }}>Cancel</Button>
            <Button onClick={() => handleSharePost(sharingPost!)}>Share</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SocialNetworking;
